import React, { useCallback, useEffect, useRef, useState } from "react";
import "./ChatBox.scss";
import searchIcon from "../../assets/Images/search-msg.png";
import fileIcon from "../../assets/Images/file-icon.svg";
import FileIcon from "../../assets/Images/FileIcon";
import userIcon from "../../assets/Images/user.png";

import ArrowLeft from "../../assets/arrowLeft.svg";
import { SendIcon, PDF } from "../../assets/Images";
import socket from "../../pages/messaging/socketConnection";
import { useSelector } from "react-redux";
import { ApiCall } from "../../Services/apis";
import ReactLinkify from "react-linkify";
import { Dropdown, Spin, message } from "antd";
import { PICTURE_URL } from "../../Services/Constants";
import { useMediaQuery } from "react-responsive";
import { useNavigate } from "react-router-dom";
import PrimaryButton from "../UI/PrimaryButton";

function ChatBox({
  selectedChat,
  setSelectedChat,
  setShowMessagesBox,
  allChats,
  setAllChats,
  setNotifications,
  notifications,
  messages,
  setMessages,
  filteredMessages,
  setFilteredMessages,
  isMessagesLoading,
  setIsMessagesLoading,
  getChatDetails,
}) {
  const navigate = useNavigate();
  const onlineUsers = useSelector(
    (state) => state.onlineUsers?.onlineUsers || []
  );
  const loader = useSelector((state) => state.general.loader);
  const inboxRef = useRef();
  const isMobile = useMediaQuery({ query: "(max-width: 760px)" });
  const user = useSelector((state) => state.auth.userData);
  const token = useSelector((state) => state.auth.userToken);
  const [messageText, setMessageText] = useState("");
  const [fileToSend, setFileToSend] = useState(null);
  const [fileKey, setFileKey] = useState(0);

  const scrollToBottom = useCallback(() => {
    const container = inboxRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }, [selectedChat?._id]);

  useEffect(() => {
    if (selectedChat?._id) {
      getChatDetails({ showLoader: true });

      socket.emit("join", { conversationId: selectedChat?._id });

      const handleChatMessage = (payload) => {
        console.log("incoming message", payload);
        if (payload?.chatId === selectedChat?._id) {
          setMessages((prev) => [...prev, payload]);
          setFilteredMessages((prev) => [...prev, payload]);

          setAllChats((prev) =>
            prev.map((chat) => {
              if (chat._id === selectedChat._id) {
                return {
                  ...chat,
                  last_message: payload,
                };
              } else {
                return chat;
              }
            })
          );

          if (payload?.sender !== user?.user_id) {
            socket.emit("messageSeen", {
              conversationId: selectedChat?._id,
              messageId: payload?._id,
            });
          }
        }
        setAllChats((prev) =>
          prev.map((chat) => {
            if (chat._id === payload?.chatId) {
              return {
                ...chat,
                last_message: payload,
              };
            } else {
              return chat;
            }
          })
        );
      };

      socket.on("chat", handleChatMessage);

      socket.on("notifications", async (notice) => {
        if (user?.user_id !== notice?.senderId) {
          console.log(notice, "message notice");
          if (notice.role === "patient") {
            setNotifications(notice?.notifications);
          }
        }
      });

      return () => {
        socket.off("chat", handleChatMessage);
      };
    }
  }, [selectedChat?._id]);

  useEffect(() => {
    scrollToBottom();
  }, [filteredMessages]);

  const sendMessage = (text) => {
    socket.emit("chat", {
      conversationId: selectedChat?._id,
      text,
      senderId: user?.user_id,
      sender_role: "Patient",
    });
  };

  const sendFile = () => {
    socket.emit(
      "upload",
      fileToSend,
      fileToSend?.name,
      fileToSend.type,
      user?.user_id,
      selectedChat?._id,
      (status) => {
        console.log(status, "status");
      },
      "Patient"
    );
  };

  socket.on("messageSeen", (payload) => {
    const updatedMessages = messages.map((message) => {
      if (message?._id === payload?._id) {
        return payload;
      } else {
        return message;
      }
    });
    setMessages(updatedMessages);
    setFilteredMessages(updatedMessages);
  });

  useEffect(() => {
    socket.emit("messageSeenAll", {
      conversationId: selectedChat?._id,
      userId: user?.user_id,
    });

    const handleSeenAll = (payload) => {
      setMessages(payload?.messages);
      setFilteredMessages(payload?.messages);
      setAllChats((prev) => {
        return prev.map((chat) => {
          if (chat._id === payload._id) {
            return {
              ...chat,
              last_message: payload?.messages?.[payload?.messages?.length - 1],
            };
          } else {
            return chat;
          }
        });
      });
    };

    socket.on("messageSeenAll", handleSeenAll);

    return () => {
      socket.off("messageSeenAll", handleSeenAll);
    };
  }, [selectedChat?._id]);

  const handleSendMessage = () => {
    if (!fileToSend) {
      if (messageText?.trim() !== "") sendMessage(messageText);
      setMessageText("");
    } else {
      sendFile();
      setFileToSend(null);
    }
  };

  const handleInputKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const onMessageSearch = (e) => {
    const value = e.target.value;
    const filtered = messages.filter((message) => {
      return message?.message?.toLowerCase().includes(value.toLowerCase());
    });

    setFilteredMessages(value ? filtered : messages);
  };

  const formatDate = (date) => {
    const options = { year: "numeric", month: "long", day: "numeric" };
    return new Date(date).toLocaleDateString(undefined, options);
  };

  const items = [
    {
      id: "0",
      label: (
        <p
          onClick={() =>
            navigate(`/patient/details/${selectedChat?.booking_id}`)
          }
        >
          View Consultation Details
        </p>
      ),
    },
  ];

  const [maxHeight, setMaxHeight] = useState("calc(100vh - 270px)"); // Initial value, can be adjusted

  useEffect(() => {
    const calculateMaxHeight = () => {
      const innerHeight = window.innerHeight;
      setMaxHeight(`calc(${innerHeight}px - 240px)`); // Adjust the value as needed
    };

    calculateMaxHeight(); // Initial calculation
    window.addEventListener("resize", calculateMaxHeight);

    return () => {
      window.removeEventListener("resize", calculateMaxHeight);
    };
  }, []);

  return (
    <div className="chat-box-main-container">
      <div className="messages-box-header">
        {selectedChat ? (
          <div className="messages-box-header-left">
            {isMobile && (
              <img
                onClick={() => {
                  setShowMessagesBox(false);
                  setSelectedChat(null);
                }}
                src={ArrowLeft}
              />
            )}
            <div className={`selected-single-chat-user-container `}>
              <div className="single-chat-user-left">
                <img
                  src={selectedChat?.doctor?.profile_image || userIcon}
                  alt={selectedChat?.doctor?.profile_image}
                />
                <div
                  className={`chat-user-online-status-circle ${
                    onlineUsers.includes(selectedChat?.doctor?.user_id)
                      ? "online-status-true"
                      : ""
                  }`}
                ></div>
              </div>
              <div className="single-chat-user-right">
                <div>
                  {" "}
                  <h4>
                    {selectedChat?.booking?.[0]?.category_id?.category_name}
                  </h4>
                  <h5>
                    {`${
                      selectedChat?.doctor?.first_name
                        ? "Dr." +
                          " " +
                          selectedChat?.doctor?.first_name +
                          " " +
                          selectedChat?.doctor?.last_name
                        : "American Clinic"
                    }`}
                  </h5>
                </div>
                <Dropdown menu={{ items }} trigger={["click"]}>
                  <div>
                    {" "}
                    <i class="fa-solid fa-ellipsis-vertical more-actions-button" />
                  </div>
                </Dropdown>
              </div>
            </div>
          </div>
        ) : (
          <h3>Messages</h3>
        )}
        {!isMobile && (
          <div className="search-input-container">
            <img src={searchIcon} alt="search-icon" />
            <input
              type="text"
              placeholder="Search in messages..."
              onChange={onMessageSearch}
            />
          </div>
        )}
      </div>
      <hr />
      {!isMessagesLoading ? (
        <>
          {selectedChat && (
            <>
              <div
                className="chat-box-messages"
                ref={inboxRef}
                style={{ maxHeight }}
              >
                {filteredMessages?.length === 0 ? (
                  <div className="no-messages-text">
                    <p>
                      No messages yet! <br />{" "}
                    </p>{" "}
                    <p className="privacy-text-inchat">
                      {" "}
                      Communicate freely with doctors, your chat is encrypted to
                      ensure privacy .
                    </p>
                  </div>
                ) : (
                  filteredMessages?.map((message, index) => {
                    const messageDate = formatDate(message?.date);
                    const prevMessageDate =
                      index > 0
                        ? formatDate(filteredMessages[index - 1]?.date)
                        : null;

                    return (
                      <React.Fragment key={message?._id}>
                        {index === 0 || messageDate !== prevMessageDate ? (
                          <div className="messages-date-divider">
                            {messageDate}
                          </div>
                        ) : null}
                        {message?.sender === user?.user_id ? (
                          <div className="single-message-item sender-message-item">
                            {/* <img
                              src={
                                selectedChat?.booking?.[0]?.personal_info
                                  ?.user_id?.profile_image ||
                                "/images/profile-img.png"
                              }
                              className="message-profile-image"
                              alt="Profile"
                            /> */}
                            <div className="single-message-text-box">
                              {message?.file?.url ? (
                                message?.file?.file_type?.startsWith(
                                  "image"
                                ) ? (
                                  <a
                                    href={`${PICTURE_URL}${message?.file?.url}`}
                                    target="_blank"
                                    download
                                  >
                                    <img
                                      src={`${PICTURE_URL}${message?.file?.url}`}
                                      className="message-image"
                                    />
                                  </a>
                                ) : (
                                  <a
                                    href={`${PICTURE_URL}${message?.file?.url}`}
                                    target="_blank"
                                    download
                                  >
                                    <PDF />
                                    <p>{message?.file?.name}</p>
                                  </a>
                                )
                              ) : (
                                <ReactLinkify
                                  componentDecorator={(
                                    decoratedHref,
                                    decoratedText,
                                    key
                                  ) => (
                                    <a
                                      className="message-as-link"
                                      target="blank"
                                      href={decoratedHref}
                                      key={key}
                                    >
                                      {decoratedText}
                                    </a>
                                  )}
                                >
                                  <p>{message?.message}</p>
                                </ReactLinkify>
                              )}
                              <span>
                                {new Date(message?.date).toLocaleTimeString(
                                  "en-US",
                                  {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  }
                                )}{" "}
                                <i
                                  className={
                                    message?.status === "seen"
                                      ? "seen"
                                      : message?.status == "delivered"
                                      ? "delivered"
                                      : ""
                                  }
                                >
                                  {message?.status}
                                </i>
                              </span>
                            </div>
                          </div>
                        ) : (
                          <div className="single-message-item">
                            <img
                              src={
                                selectedChat?.doctor?.profile_image || userIcon
                              }
                              alt="Profile"
                              className="message-profile-image"
                            />
                            <div className="single-message-text-box">
                              {message?.sender_role && (
                                <h6>{message?.sender_role}</h6>
                              )}
                              {message?.file?.url ? (
                                message?.file?.file_type?.startsWith(
                                  "image"
                                ) ? (
                                  <a
                                    href={`${PICTURE_URL}${message?.file?.url}`}
                                    target="_blank"
                                  >
                                    <img
                                      src={`${PICTURE_URL}${message?.file?.url}`}
                                      className="message-image"
                                    />
                                  </a>
                                ) : (
                                  <a
                                    href={`${PICTURE_URL}${message?.file?.url}`}
                                    target="_blank"
                                  >
                                    <PDF />
                                    <p>{message?.file?.name}</p>
                                  </a>
                                )
                              ) : (
                                <ReactLinkify
                                  componentDecorator={(
                                    decoratedHref,
                                    decoratedText,
                                    key
                                  ) => (
                                    <a
                                      className="message-as-link"
                                      target="blank"
                                      href={decoratedHref}
                                      key={key}
                                    >
                                      {decoratedText}
                                    </a>
                                  )}
                                >
                                  <p>{message?.message}</p>
                                </ReactLinkify>
                              )}
                              <span>
                                {new Date(message?.date).toLocaleTimeString(
                                  "en-US",
                                  {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  }
                                )}
                              </span>
                            </div>
                          </div>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </div>
              <div className="chat-box-messages-sending-container">
                <div className="messages-inputs-container">
                  {!fileToSend ? (
                    <input
                      placeholder="Type something..."
                      type="text"
                      disabled={fileToSend ? true : false}
                      value={messageText}
                      onChange={(e) => setMessageText(e.target.value)}
                      onKeyDown={handleInputKeyDown}
                    />
                  ) : (
                    <div className="file-name">
                      <span>{fileToSend?.name}</span>
                      <span
                        className="cross-file-name"
                        onClick={() => setFileToSend(null)}
                      >
                        X
                      </span>
                    </div>
                  )}
                  <input
                    id="message-file-icon"
                    key={fileKey}
                    type="file"
                    hidden
                    style={{ display: "none" }}
                    onChange={(e) => {
                      if (
                        e.target?.files[0]?.type === "application/pdf" ||
                        e.target?.files[0]?.type.split("/")[0] === "image"
                      ) {
                        setFileToSend(e.target.files[0]);
                        setFileKey((prevKey) => prevKey + 1);
                        return;
                      }
                      message.error("Only Images and PDF files Allowed");
                    }}
                  ></input>

                  <label className="file-icon" htmlFor="message-file-icon">
                    <img
                      height={20}
                      width={20}
                      src={fileIcon}
                      alt="file-icon"
                    />
                  </label>
                </div>
                <PrimaryButton icon={<SendIcon />} onClick={handleSendMessage}>
                  Send
                </PrimaryButton>
              </div>
            </>
          )}
          {!(allChats?.length > 0) && !loader && (
            <div className="no-chat-message ">
              <p>Your chat history with doctors is empty</p>
            </div>
          )}
        </>
      ) : (
        <div className="no-chat-message ">
          <Spin />{" "}
        </div>
      )}
    </div>
  );
}

export default ChatBox;
